import { useState, useMemo, useRef, useEffect, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import useFundContext from '@/contexts/Fund/useFundContext';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { ContactDtoIn } from '@/models/in/ContactDtoIn';
import SearchInput from '@/components/ui/SearchInput/SearchInput';
import SupportedListItem from './SupportedListItem';
import Button from '@/components/ui/Button';
import styles from './ChooseSupported.module.scss';

interface ChooseSupportedProps {
  onStart: (supported: ContactDtoIn) => void;
  /**
   * HAT-1453: a beneficiary who is not in the list used to be a dead end - close this, go to the
   * supporteds screen, add them there, come back and start over. When given, the add button sits
   * at the top of the list and again where the list says there is nobody to choose.
   */
  onAddSupported?: () => void;
}

const ChooseSupported = ({ onStart, onAddSupported }: ChooseSupportedProps) => {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();
  const [selectedSupported, setSelectedSupported] = useState<ContactDtoIn | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null) as RefObject<HTMLInputElement>;

  /*
   * HAT-1386: הדיאלוג נפתח כדי לבחור נתמך, והדרך לבחור היא להקליד את שמו.
   * MainDialogLayout מבטל את הפוקוס האוטומטי של Radix (אחרת הוא נוחת על
   * כפתור הסגירה), כך שבלי זה הרכז היה צריך ללחוץ על החיפוש לפני ההקלדה.
   *  - במכשיר מגע לא ממקדים: המקלדת הייתה עולה ומכסה את רשימת הנתמכים.
   *  - פוקוס על לשונית = חזרה מ"בקשה עבור הקרן" במקלדת. גניבת הפוקוס
   *    משם הייתה שוברת את החיצים שעוברים בין הלשוניות.
   */
  useEffect(() => {
    if (!window.matchMedia?.('(pointer: fine)').matches) return;
    if (document.activeElement?.closest('[role="tablist"]')) return;
    searchInputRef.current?.focus();
  }, []);

  const { data: supporteds, isLoading } = useGetAuthApi<ContactDtoIn[]>(
    currentFund?.id ? `funds/${currentFund.id}/supporteds/basic` : ''
  );

  const filteredSupporteds = useMemo(() => {
    if (!supporteds) return [];
    if (!searchQuery.trim()) return supporteds;

    const query = searchQuery.toLowerCase();
    return supporteds.filter((supported) => {
      const fullName = `${supported.firstName ?? ''} ${supported.lastName ?? ''}`.toLowerCase();
      return fullName.includes(query) || (supported.israeliId ?? '').includes(query);
    });
  }, [supporteds, searchQuery]);

  const handleSupportedClick = (supported: ContactDtoIn) => {
    setSelectedSupported(supported);
  };

  /*
   * Same rule as the supporteds screen's own add button: a welfare fund that already has its
   * supported does not take another.
   */
  const canAddSupported = !!onAddSupported && !(currentFund?.classification === 'Welfare' && (supporteds?.length ?? 0) > 0);

  const addSupportedButton = (
    <Button variant="primary" onClick={onAddSupported} className={styles.addSupportedButton}>
      {t('addSupported')}
    </Button>
  );

  const handleStartClick = () => {
    if (selectedSupported) {
      onStart(selectedSupported);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.contentArea}>
        {/* השאלה שפותחת את השלב במקור (.req-question). מוצגת במסך צר בלבד. */}
        <div className={styles.headerRow}>
          <h2 className={styles.question}>{t('wizardForWhom')}</h2>
          {canAddSupported && addSupportedButton}
        </div>
        <div className={styles.searchContainer}>
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={t('searchSupported')}
            tooltipText="searchSupportedTooltip"
            inputRef={searchInputRef}
          />
        </div>

        <div className={styles.listContainer}>
          {isLoading ? (
            <div className="p-6 text-center">טוען...</div>
          ) : filteredSupporteds.length === 0 ? (
            <div className="p-6 text-center">
              {searchQuery ? t('noSupportedsFound') : t('noSupportedsInFund')}
              {canAddSupported && (
                <p className="mt-3 body-sm-regular text-(--font-body-3)">{t('supportedNotInListHint')}</p>
              )}
            </div>
          ) : (
            <>
              {filteredSupporteds.map((supported) => (
                <SupportedListItem
                  key={supported.id}
                  supported={supported}
                  isSelected={selectedSupported?.id === supported.id}
                  onClick={handleSupportedClick}
                />
              ))}
            </>
          )}
        </div>
      </div>
      <footer className={styles.footer}>
        {/*
         * מחוון השלבים של המקור (.req-bar). במסך צר בלבד. בחירת הנתמך היא
         * השלב הראשון מתוך ארבעה בעיני מי שמחזיק את הטלפון — גם אם בקוד
         * היא מסך פתיחה שלפני ה-FormStepper. תצוגה בלבד; הזרימה לא משתנה.
         */}
        <div className={styles.stepDots} aria-hidden="true">
          <span className={`${styles.stepDot} ${styles.stepDotActive}`} />
          <span className={styles.stepDot} />
          <span className={styles.stepDot} />
          <span className={styles.stepDot} />
        </div>
        <button className={styles.startButton} onClick={handleStartClick} disabled={!selectedSupported}>
          <span className={`heading-h4-semibold ${!selectedSupported ? 'text-(--font-on-disabled)' : 'text-(--font-body-4)'}`}>{t('start')}</span>
        </button>
      </footer>
    </div>
  );
};

export default ChooseSupported;

